/** @file Starts an animation on any track 0-31 (base or mirrorFix layer), mixing from the current entry. */
import type { AnimState, TrackEntry } from '../contracts/state';
import type { SkeletonData } from '../skeletonData';
import { ensureTrack } from './ensureTrack';
import { findAnimation } from './findAnimation';
import { mixDurationFor } from './mixDurationFor';
import { newTrackEntry } from './newTrackEntry';

/**
 * Internal entry point for tracks [0, 32) (R2-06): setAnimation (base tracks) and core E's playDirectional
 * (mirrorFix layers 16 + t) both start entries here. The new entry mixes from the current one (keeping its
 * from-chain) with fresh per-bone mixDirs; a current entry that never advanced is dropped and its own `from` is
 * used instead. mixDuration defaults from data.mixDefaults. Clears the track's queue. NOT_FOUND at `anim`.
 */
export function startEntry(state: AnimState, data: SkeletonData, track: number, anim: string, opts?: { loop?: boolean; mixDuration?: number }): TrackEntry {
  const index = findAnimation(data, anim, 'anim');
  const queue = ensureTrack(state, track);
  const current = state.tracks[track] ?? null;
  const from = current !== null && current.lastTime < 0 ? current.from : current;
  const mixDuration = opts?.mixDuration ?? (from === null ? 0 : mixDurationFor(data, from.anim, index));
  const entry = newTrackEntry(index, opts?.loop ?? false, mixDuration, 0);
  entry.from = from;
  entry.mixDirs = from === null ? null : new Float64Array(data.bones.length);
  state.tracks[track] = entry;
  queue.length = 0;
  return entry;
}
